import { useCallback } from 'react';
import { Linking, Platform } from 'react-native';

import { AlertDialog, Button, createMqStyles } from '@fhs/ui';

const TITLE = 'Turn On Location Services';
const BODY = 'Allow location access in your device or browser settings to find nearby restaurants.';
const canOpenSettings = Platform.OS !== 'web';

interface LocationServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Denied-location dialog for the v2 locator. AlertDialog already matches the modal chrome
 * from the design; this wrapper supplies the copy, an Open Settings CTA on native, and Close.
 */
export function LocationServicesModal({ isOpen, onClose }: LocationServicesModalProps) {
  const styles = useLocationServicesModalMqStyles();

  const openSettings = useCallback(async () => {
    if (!canOpenSettings) {
      return;
    }

    onClose();

    try {
      await Linking.openSettings();
    } catch {
      // Native settings are unavailable (for example on web).
    }
  }, [onClose]);

  return (
    <AlertDialog
      visible={isOpen}
      accessibilityLabel={TITLE}
      headingContent={TITLE}
      bodyContent={BODY}
      hideCloseIcon
      onClose={onClose}
      onRequestClose={onClose}
      testID="geolocation-denied-modal"
      actions={
        <>
          {canOpenSettings ? (
            <Button
              size="xl"
              type="solid"
              onPress={openSettings}
              style={styles.actionButton}
              testID="geolocation-denied-open-settings"
            >
              <Button.Text>Open Settings</Button.Text>
            </Button>
          ) : null}
          <Button
            size="xl"
            type={canOpenSettings ? 'outline' : 'solid'}
            onPress={onClose}
            style={styles.actionButton}
            testID="geolocation-denied-close"
          >
            <Button.Text>Close</Button.Text>
          </Button>
        </>
      }
    />
  );
}

const useLocationServicesModalMqStyles = createMqStyles({
  actionButton: {
    $base: {
      width: '100%',
    },
    $gteDesktop: {
      flex: 1,
    },
  },
});
